/**
 * The Walliyo mark, as geometry (WALLIYO-0137).
 *
 * Three things draw it — the asset generator (`scripts/brand-assets.ts`), the
 * console (`admin/src/shell/BrandMark.tsx`) and the app
 * (`client/src/ui/components/BrandMark.tsx`) — so the shapes live here once
 * instead of being retyped into an SVG file, a React component and a
 * react-native-svg component that then drift apart.
 *
 * A W whose four strokes are the trades: a spanner on the left, a pair of
 * scissors turned over in the middle (its handle loops at the W's peak, its
 * blades running down to the bottom corners), a broom on the right with its
 * head at the top, under a hard hat worn tilted whose band is a tape measure,
 * with the verified badge hanging below.
 *
 * The W is four separate strokes rather than one polyline because a spanner's
 * jaw and a broom's head are *ends*, and a continuous W has only two free ends.
 *
 * Each tool is drawn as the tool, not as a stroke with a dot on it: the jaw
 * grips the end of its own handle, the bristles flare away from the ferrule,
 * the handle loops are ovals lying along their blades. A circle at the end of a
 * line reads as a circle at the end of a line.
 */

/** The box every coordinate below is in. */
export const MARK_BOX = { x: 0, y: 14, width: 120, height: 122 } as const;

/** The hat is worn tilted; its parts carry this, nothing else does. */
export const HAT_TILT = { angle: -7, x: 58, y: 34 } as const;

/** `ink` is the hat and the four strokes; `accent` is the tools' working ends. */
export type MarkTone = "ink" | "accent";

export type MarkPart =
  | { shape: "path"; d: string; fill: MarkTone }
  | { shape: "path"; d: string; stroke: MarkTone; width: number; round?: boolean }
  | { shape: "rect"; x: number; y: number; width: number; height: number; fill: MarkTone }
  | { shape: "circle"; cx: number; cy: number; r: number; fill: MarkTone }
  | { shape: "circle"; cx: number; cy: number; r: number; stroke: MarkTone; width: number }
  | { shape: "ellipse"; cx: number; cy: number; rx: number; ry: number; turn: number; stroke: MarkTone; width: number };

/**
 * Below about 48px the fine work turns to mush, so it is dropped and what is
 * left is thickened. A mark with one drawing does not survive a favicon.
 */
export type MarkDetail = "full" | "simple";

const WIDTHS = {
  full: { stroke: 11, blade: 9, jaw: 7, loop: 4 },
  simple: { stroke: 12, blade: 10, jaw: 8, loop: 5 },
} as const;

/**
 * The hard hat, in profile.
 *
 * A tall crown and a brim that tapers to a peak at the front: flatten the crown
 * or run the brim straight and it stops being a hard hat and becomes a boater.
 * The brim's back end is shallow, its front end juts, which is the silhouette
 * people actually recognise.
 */
export function hatParts(detail: MarkDetail = "full"): MarkPart[] {
  void detail;
  return [
    { shape: "path", d: "M32 41 C32 22 42 15 55 15 C67 15 73 25 73 41 Z", fill: "ink" },
    { shape: "path", d: "M26.5 41 L92 42.4 Q96.5 43.2 91.5 46.6 L29.5 45.4 Q24.5 44.6 26.5 41 Z", fill: "ink" },
    // The band is a tape measure; its ticks are cut out of it, not painted on.
    { shape: "rect", x: 32.4, y: 34, width: 41.2, height: 6.2, fill: "accent" },
  ];
}

/** Everything under the hat: the spanner, the scissors, the broom, the badge. */
export function bodyParts(detail: MarkDetail = "full"): MarkPart[] {
  const w = WIDTHS[detail];
  const badgeRim: MarkPart[] =
    detail === "simple"
      ? []
      : (
          [
            [70.5, 119],
            [67.42, 126.42],
            [60, 129.5],
            [52.58, 126.42],
            [49.5, 119],
            [52.58, 111.58],
            [60, 108.5],
            [67.42, 111.58],
          ] as const
        ).map(([cx, cy]): MarkPart => ({ shape: "circle", cx, cy, r: 3.2, fill: "accent" }));

  return [
    // Left stroke: the spanner. The jaw is centred ON the end of its handle, so
    // it grips it — set beyond the end it reads as a hook hanging in the air.
    { shape: "path", d: "M14 54 L38.03 96.52", stroke: "ink", width: w.stroke, round: true },
    { shape: "path", d: "M15.39 44.46 A7.5 7.5 0 1 1 5.1 50.28", stroke: "accent", width: w.jaw, round: true },

    // The middle: scissors upside down. The blades cross just under the W's
    // peak and end exactly on its bottom corners, so the letter is unchanged.
    { shape: "path", d: "M56 64 L78 97", stroke: "ink", width: w.blade, round: true },
    { shape: "path", d: "M64 64 L42 97", stroke: "ink", width: w.blade, round: true },
    ...(detail === "simple" ? [] : [{ shape: "circle", cx: 60, cy: 70, r: 3, fill: "accent" } as MarkPart]),
    // The handle loops are ovals lying along their own blades. Two equal
    // circles sitting side by side read as a pair of eyes, which is not what a
    // mark under a hat can afford.
    { shape: "ellipse", cx: 52.56, cy: 58.84, rx: 6.6, ry: 4.5, turn: 56.3, stroke: "accent", width: w.loop },
    { shape: "ellipse", cx: 67.44, cy: 58.84, rx: 6.6, ry: 4.5, turn: -56.3, stroke: "accent", width: w.loop },

    // Right stroke: the broom, head at the top so it answers the spanner. The
    // head is a flare, narrow at the ferrule and wide at the bristles — a thick
    // line at an angle is a stick.
    { shape: "path", d: "M81.97 96.52 L106 54", stroke: "ink", width: w.stroke, round: true },
    { shape: "path", d: "M110.79 56.71 L117.78 51.46 L102.1 42.6 L101.21 51.29 Z", fill: "accent" },

    // The badge: a person checked this pro, so the mark wears the proof.
    { shape: "path", d: "M50 101 L56 110", stroke: "accent", width: 4.5, round: true },
    { shape: "path", d: "M70 101 L64 110", stroke: "accent", width: 4.5, round: true },
    ...badgeRim,
    { shape: "circle", cx: 60, cy: 119, r: 11, fill: "accent" },
  ];
}

/**
 * The holes.
 *
 * The tape's ticks, the ferrule binding the broom's bristles and the check on
 * the badge are painted by whatever is behind the mark, not by ink. Cutting
 * them as real holes is what lets ONE file sit on cream, white, sand or a
 * photograph instead of needing a copy per background.
 *
 * `tilted` holes are cut inside the hat's rotation; the rest are not.
 */
export function holeParts(detail: MarkDetail = "full"): { tilted: MarkPart[]; flat: MarkPart[] } {
  const ticks =
    "M37 35.1 V39.1 M42 35.1 V39.1 M47 35.1 V39.1 M52 35.1 V39.1 M57 35.1 V39.1 M62 35.1 V39.1 M67 35.1 V39.1";
  const check: MarkPart = {
    shape: "path",
    d: "M54.8 119.2 L58.6 123 L65.4 114.8",
    stroke: "ink",
    width: 3,
    round: true,
  };
  if (detail === "simple") return { tilted: [], flat: [check] };
  return {
    tilted: [{ shape: "path", d: ticks, stroke: "ink", width: 1.6 }],
    flat: [{ shape: "path", d: "M110.79 56.71 L101.21 51.29", stroke: "ink", width: 3, round: true }, check],
  };
}

/** The three colours, and nothing else. */
export const BRAND = {
  green: "#0D3B2E",
  cream: "#F5F0E6",
  gold: "#D4A853",
} as const;

/**
 * The official mark is cream on the deep green. The other two follow from it:
 * `onLight` for a pale ground, `mono` for one ink — print, embroidery, a stamp.
 */
export const MARK_COLOURWAYS = {
  onDark: { ink: BRAND.cream, accent: BRAND.gold },
  onLight: { ink: BRAND.green, accent: BRAND.gold },
  mono: { ink: BRAND.green, accent: BRAND.green },
} as const;

export type MarkColourway = keyof typeof MARK_COLOURWAYS;
